import { z } from 'zod';
import { Instant, Locale, LocalDate, MinorAmount, Phone, TenantSlug, TimeZone, Uuid } from './common.js';
// Контракт онлайн-записи: поиск слотов → удержание → создание → перенос/отмена.
// Одни и те же схемы валидируют вход на сервере и дают типы витрине, кабинету,
// ботам и мобильному приложению: второго описания этих тел запросов не существует.
// Откуда пришла запись. Значения дословно повторяют enum booking_source в базе:
// от источника зависят лимиты (у бота одни, у администратора другие) и аналитика.
export const BookingSource = z.enum(['web', 'telegram', 'max', 'mobile', 'widget', 'admin', 'api']);
/**
 * Источник записи, который может назвать ПУБЛИЧНАЯ форма: страница, виджет, боты, приложение.
 * «admin» и «api» — это кабинет и интеграция, у них свои пути; с витрины они были бы
 * подделкой — запись «от администратора», которую оформил человек из интернета. Та же граница
 * стоит в базе, в политике вставки записи для роли витрины (03-rls-policies.sql, 4.12).
 */
export const OnlineBookingSource = z.enum(['web', 'telegram', 'max', 'mobile', 'widget']);
// Статусы записи — enum appointment_status. Переходы сторожит доменный слой, не схема.
export const AppointmentStatus = z.enum([
    'pending',
    'confirmed',
    'in_progress',
    'completed',
    'cancelled',
    'no_show',
]);
// Кто инициировал отмену или перенос. Различие не косметическое: отмена клиентом
// тянет счётчик неявок и карантин слота, отмена салоном — нет (GAPS C1).
export const CancellationActor = z.enum(['client', 'staff', 'system']);
// Зачем клиент удерживает слот. Публичному контуру доступны только эти виды:
// payment и quarantine ставит сервер сам, снаружи их запросить нельзя.
export const SlotHoldKind = z.enum(['checkout', 'reschedule', 'waitlist', 'manual']);
/**
 * Удержание, которое может поставить ПУБЛИЧНАЯ форма. «manual» — это администратор, придержавший
 * время звонящему клиенту; с витрины такое удержание выдавало бы себя за стойку. Та же граница —
 * в политике вставки удержания для роли витрины (03-rls-policies.sql, 4.13).
 */
export const PublicSlotHoldKind = z.enum(['checkout', 'reschedule', 'waitlist']);
// Значение заголовка Idempotency-Key (ADR-032, рубеж 1). Второе нажатие «Записаться»
// и ретрай бота обязаны вернуть ту же запись, а не создать вторую.
export const IdempotencyKey = z.string().min(8).max(255);
// Корзина услуг одной записи. Несколько услуг сразу — требование рынка (GAPS C3),
// верхняя граница защищает расчёт слотов от запроса на полсотни услуг разом.
export const ServiceIds = z.array(Uuid).min(1).max(10);
// ───────────────────────── поиск свободных слотов ─────────────────────────
/**
 * Запрос свободного времени.
 *
 * Границы периода — календарные даты, а не моменты: клиент выбирает «15 октября»,
 * и что это за интервал абсолютного времени, решает PostgreSQL через AT TIME ZONE
 * зоны филиала (ADR-007 п.6). Прислать сюда «15 октября 00:00+03» означало бы
 * зафиксировать смещение на стороне клиента — ровно та ошибка, которая ломает
 * выдачу у филиала в другом часовом поясе.
 */
export const SlotSearchQuery = z
    .object({
    // Салон в публичном адресе (<slug>.planerius.ru). Используется витриной и ботами,
    // которые про внутренние идентификаторы ничего не знают.
    tenantSlug: TenantSlug.optional(),
    // Конкретная точка. Без неё поиск идёт по всем активным филиалам салона,
    // и тогда у каждого дня в ответе своя зона.
    branchId: Uuid.optional(),
    serviceIds: ServiceIds,
    // Пусто — «любой свободный мастер»: самый частый и самый дорогой запрос продукта.
    employeeId: Uuid.optional(),
    // Пожелание по конкретному ресурсу («тот самый кабинет»). Обычно ресурс
    // подбирает планировщик по требованиям услуги, а не клиент.
    resourceId: Uuid.optional(),
    dateFrom: LocalDate,
    dateTo: LocalDate,
    // Зона устройства клиента. Нужна не для расчёта, а для подписи в интерфейсе:
    // «14:30 по времени салона, у вас 12:30» (ADR-007 п.8).
    clientTimeZone: TimeZone.optional(),
})
    .refine((q) => q.tenantSlug !== undefined || q.branchId !== undefined, {
    error: 'нужен либо tenantSlug, либо branchId',
    path: ['tenantSlug'],
})
    // Сравнение строк ISO-даты лексикографически совпадает с календарным порядком.
    .refine((q) => q.dateFrom <= q.dateTo, {
    error: 'dateFrom не может быть позже dateTo',
    path: ['dateTo'],
});
/**
 * Один предлагаемый слот. Мастер здесь всегда конкретный: даже при поиске
 * «любой мастер» витрина показывает время вместе с тем, кто его отработает,
 * иначе клиент не понимает, к кому записался.
 */
export const SlotItem = z.object({
    start: Instant,
    end: Instant,
    employeeId: Uuid,
    // Цена именно этого слота, а не услуги из каталога: на неё уже наложены
    // персональные переопределения мастера и правила «счастливых часов».
    priceMinor: MinorAmount,
    // Длительность работы мастера без буферов и уборки — то, что видит клиент.
    durationMinutes: z.number().int().positive(),
});
// Слоты одного календарного дня филиала. День выделен в отдельную сущность,
// потому что пустой день («выходной») — это тоже ответ, а не отсутствие данных.
export const SlotDay = z.object({
    date: LocalDate,
    slots: z.array(SlotItem),
});
/**
 * Ответ на поиск слотов.
 *
 * Зона филиала и шаг сетки отдаются вместе со слотами, а не запрашиваются отдельно:
 * без них клиент не может ни подписать время, ни нарисовать сетку, и сделает это
 * по своей копии tzdata — которая отличается от базы PostgreSQL (ADR-007 п.6).
 */
export const SlotSearchResult = z.object({
    branchId: Uuid,
    timezone: TimeZone,
    // Шаг решётки стартов в минутах (branches.slot_step_minutes).
    slotStepMinutes: z.number().int().positive(),
    // Границы онлайн-записи филиала: почему ближайшие часы и далёкие даты пусты.
    minLeadMinutes: z.number().int().nonnegative(),
    maxHorizonDays: z.number().int().positive(),
    days: z.array(SlotDay),
});
// ───────────────────────── удержание слота ─────────────────────────
/**
 * Удержание слота на время оформления (GAPS C2).
 *
 * Без него слот уводят из-под клиента, пока он вводит код из SMS. Удерживаем ровно
 * то, что потом станет записью: точку, мастера и время начала — конец сервер считает
 * сам по длительностям услуг, доверять её клиенту нельзя.
 */
export const CreateSlotHoldRequest = z.object({
    branchId: Uuid,
    serviceIds: ServiceIds,
    employeeId: Uuid.optional(),
    resourceId: Uuid.optional(),
    startAt: Instant,
    kind: PublicSlotHoldKind.default('checkout'),
    // Пожелание по времени жизни. Сервер обрежет его по booking_rules.hold_ttl_seconds:
    // иначе клиент запросит холд на сутки и заблокирует день салону.
    ttlSeconds: z.number().int().min(30).max(1800).optional(),
});
// Выданное удержание. expiresAt — абсолютный момент, а не «осталось N секунд»:
// секунды успевают протухнуть в пути и в спящем телефоне.
export const SlotHoldResponse = z.object({
    holdId: Uuid,
    branchId: Uuid,
    employeeId: Uuid.nullable(),
    startAt: Instant,
    endAt: Instant,
    expiresAt: Instant,
    timezone: TimeZone,
});
// Явный отказ от удержания: клиент вернулся назад в мастере записи.
// Ждать истечения TTL в этом случае — терять живой слот на несколько минут.
export const ReleaseSlotHoldRequest = z.object({ holdId: Uuid });
// ───────────────────────── создание записи ─────────────────────────
/**
 * Кто записывается: либо уже известная салону карточка, либо новый человек
 * с именем и телефоном. Телефон строго E.164 — по нему идёт и поиск, и дедупликация,
 * любой другой формат ломает и то и другое.
 */
export const BookingClient = z.union([
    z.object({ clientId: Uuid }),
    z.object({
        // Имя из формы записи попадает дословно в ленту салона («Анна записалась на 15:00…»).
        // Ссылка в нём — это «подтвердите оплату по ссылке» от имени системы, пришедшее честной
        // записью, без всякого взлома; поэтому ссылок в имени нет (и в базе тоже, 4.10).
        name: z
            .string()
            .trim()
            .min(1)
            .max(200)
            .refine((value) => !/:\/\/|www\./iu.test(value), {
            error: 'В имени не может быть ссылки',
        }),
        phone: Phone,
        // Язык напоминаний по этой записи; по умолчанию берётся язык салона.
        locale: Locale.optional(),
    }),
]);
/**
 * Согласия клиента. Два РАЗНЫХ поля, и объединять их в один чекбокс нельзя:
 * с 01.09.2025 согласие на обработку ПД оформляется отдельным документом,
 * а рекламная рассылка без отдельного согласия на рекламу — штраф ФАС салону.
 * Версия текста обязательна: через год иначе не доказать, на что человек согласился.
 */
export const BookingConsents = z
    .object({
    // Ровно true: без согласия на обработку ПД запись создать невозможно в принципе.
    personalData: z.literal(true),
    personalDataVersion: z.string().min(1).max(64),
    // Отдельное, добровольное и по умолчанию невыданное согласие на рекламу.
    ads: z.boolean().default(false),
    adsVersion: z.string().min(1).max(64).optional(),
})
    .refine((c) => !c.ads || c.adsVersion !== undefined, {
    error: 'для рекламного согласия обязательна версия текста',
    path: ['adsVersion'],
});
/**
 * Создание записи. Конец визита и итоговая цена сюда не передаются: их считает
 * сервер по услугам, шагам и правилам цены, а присланные клиентом значения были бы
 * способом записаться на час дешевле.
 */
export const CreateAppointmentRequest = z.object({
    branchId: Uuid,
    // Пусто = «любой мастер»: сервер выберет исполнителя сам либо оставит запись
    // неразобранной, если так настроен филиал (GAPS C5).
    employeeId: Uuid.optional(),
    serviceIds: ServiceIds,
    startAt: Instant,
    client: BookingClient,
    comment: z.string().trim().max(1000).optional(),
    source: OnlineBookingSource,
    // Удержание, выданное на шаге оформления. Без него запись тоже создастся,
    // но проигрыш гонки за слот станет заметно вероятнее.
    holdId: Uuid.optional(),
    consents: BookingConsents,
    // Зона устройства — только для подписи времени в подтверждении.
    clientTimeZone: TimeZone.optional(),
});
// ───────────────────────── перенос и отмена ─────────────────────────
/**
 * Перенос — самостоятельная операция, а не «отменить и создать заново» (GAPS C6):
 * иначе теряется история и перенос считается отменой в статистике клиента.
 */
export const RescheduleAppointmentRequest = z.object({
    appointmentId: Uuid,
    startAt: Instant,
    // Смена мастера вместе с переносом — обычный сценарий при болезни исполнителя.
    // Пусто = мастер не меняется.
    employeeId: Uuid.optional(),
    // Удержание нового слота. Берётся ДО отпускания старого, иначе клиент
    // останется вообще без времени, если новый слот уведут в момент переноса.
    holdId: Uuid.optional(),
    initiatedBy: CancellationActor,
    comment: z.string().trim().max(1000).optional(),
    /**
     * Отправить клиенту сообщение о переносе.
     *
     * Живёт ЗДЕСЬ, а не только в кабинетной надстройке, потому что решение доезжает
     * до службы записи — она единственная точка, где строится план уведомлений. Онлайн-
     * перенос клиентом всегда true: человек переносит сам и подтверждение ждёт.
     */
    notifyClient: z.boolean().default(true),
});
/**
 * Отмена. Причина — код из справочника салона: именно он решает, считать ли отмену
 * неявкой, сгорает ли предоплата и уходит ли слот в карантин. Свободный текст
 * этих решений принять не может, поэтому он только дополнение.
 */
export const CancelAppointmentRequest = z
    .object({
    appointmentId: Uuid,
    initiatedBy: CancellationActor,
    reasonCode: z.string().min(1).max(64).optional(),
    comment: z.string().trim().max(1000).optional(),
    /**
     * Отправить клиенту сообщение об отмене.
     *
     * По умолчанию TRUE и почти всегда true: клиент, не узнавший об отмене, приедет
     * к закрытой двери. Снять галочку администратор вправе в одном случае — когда сам
     * только что говорил с человеком по телефону.
     */
    notifyClient: z.boolean().default(true),
})
    .refine((r) => r.initiatedBy !== 'staff' || r.reasonCode !== undefined, {
    error: 'отмена салоном требует кода причины',
    path: ['reasonCode'],
});
// ───────────────────────── представление записи ─────────────────────────
// Услуга внутри записи — снимок на момент создания: прайс потом меняется,
// а сумма в истории клиента меняться не должна.
export const AppointmentServiceView = z.object({
    serviceId: Uuid,
    name: z.string(),
    durationMinutes: z.number().int().positive(),
    priceMinor: MinorAmount,
    // Исполнитель именно этой услуги: в корзине их может быть несколько разных.
    employeeId: Uuid.nullable(),
    // Окно услуги, когда услуги идут цепочкой; у одиночной совпадает с записью.
    startAt: Instant.nullable(),
    endAt: Instant.nullable(),
});
// Филиал в карточке записи: ровно то, что нужно клиенту, чтобы доехать
// и правильно прочитать время.
export const AppointmentBranchView = z.object({
    id: Uuid,
    name: z.string(),
    timezone: TimeZone,
    address: z.string().nullable(),
});
// Мастер в карточке записи — публичное имя и фото. Внутреннего имени,
// телефона и цвета колонки журнала здесь нет: это данные салона, не клиента.
export const AppointmentEmployeeView = z.object({
    id: Uuid,
    publicName: z.string(),
    positionName: z.string().nullable(),
    avatarUrl: z.url().nullable(),
});
/**
 * Запись глазами клиента — общий ответ для создания, переноса, отмены и просмотра.
 *
 * Сроки отмены и переноса отдаются готовыми моментами, а не «за 180 минут»: правило
 * живёт в booking_rules филиала, и считать его повторно в четырёх клиентах — это
 * четыре шанса разойтись с сервером.
 */
export const AppointmentView = z.object({
    id: Uuid,
    status: AppointmentStatus,
    startAt: Instant,
    endAt: Instant,
    // Зона филиала: время показываем в ней и подписываем, если она отличается
    // от зоны устройства (ADR-007 п.8).
    timezone: TimeZone,
    branch: AppointmentBranchView,
    // Пусто у неразобранной записи и у групповой сессии.
    employee: AppointmentEmployeeView.nullable(),
    services: z.array(AppointmentServiceView).min(1),
    priceMinor: MinorAmount,
    prepaidMinor: MinorAmount,
    comment: z.string().nullable(),
    source: BookingSource,
    createdAt: Instant,
    cancelDeadlineAt: Instant.nullable(),
    rescheduleDeadlineAt: Instant.nullable(),
    canCancelOnline: z.boolean(),
    canRescheduleOnline: z.boolean(),
    // Подписанные одноразовые ссылки для клиента без входа в аккаунт (GAPS E6).
    // По ним доступна только эта запись и только отмена/перенос — ссылку пересылают,
    // и ни телефон, ни другие визиты человека за ней открываться не должны.
    cancelUrl: z.url().nullable(),
    rescheduleUrl: z.url().nullable(),
});
//# sourceMappingURL=booking.js.map